import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import useMetaSingle from "../../data/useMetaSingle";
import useSocialSchedules from "../../data/useSocialSchedules";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import SwitchInput from "../Shared/Inputs/SwitchInput";
import LoadingWheel from "../Shared/LoadingWheel";
import { TextInput } from "../Shared/Inputs/TextInput";
import { NumberInput } from './../Shared/Inputs/NumberInput';


export default function SocialSettings() {
  const { agencyId, pageId } = useParams();
  const { metaPageConfig, update: updateConfig } = useMetaSingle(agencyId, pageId);
  const { socialSchedules, add, update, remove } = useSocialSchedules(agencyId);
  const { saveHandlers } = useDefaultCRUDHandlers("Social Page Settings");
  const [schedules, setSchedules] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const { register, handleSubmit, setValue, getValues, watch, reset, formState: { errors } } = useForm({
    defaultValues: {
      isActive: true,
      socialPageSettings: {
        evergreensEnabled: true,
        infographicsEnabled: true,
        reviewsEnabled: true,
        newsEnabled: true,
        salesPropertiesEnabled: true,
        lettingsPropertiesEnabled: true,
        soldPropertiesEnabled: true,
        letPropertiesEnabled: true,
        reducedPropertiesEnabled: true,
        blogPostLimit: null,
        reviewLimit: null,
        newPropertyLimit: null,
        reducedPropertyLimit: null,
        soldLetPropertyLimit: null,
      }
    }
  });

  const handleIsActive = (registerName, e) => {
    const updatedConfig = {
      ...metaPageConfig.data.data,
      isActive: e,
    };

    setValue(registerName, e);

    updateConfig.mutate(updatedConfig);
  };


  const onSubmit = (data) => {
    console.log(data);

    const updatedConfig = {
      ...metaPageConfig.data.data,
      ...data,
      socialPageSettings: {
        ...metaPageConfig.data.data.socialPageSettings,
        ...data.socialPageSettings
      }
    };

    updateConfig.mutate(updatedConfig, saveHandlers);
  };


  useEffect(() => {
    if (!socialSchedules.isLoading && socialSchedules.data && !metaPageConfig.isLoading && metaPageConfig.data) {
      setSchedules(socialSchedules.data);
      reset(metaPageConfig.data.data);
      setIsLoading(false);
    };
  }, [socialSchedules.data, socialSchedules.isLoading, metaPageConfig.data, metaPageConfig.isLoading])

  if (isLoading) {
    return (
      <div className="flex p-10 bg-white justify-center">
        <LoadingWheel width="w-12" height="h-12" />
      </div>
    )
  }

  return (
    <form className="flex flex-col gap-y-6" onSubmit={handleSubmit(onSubmit)}>

      <div className="flex justify-between">
        <h1 className="text-lg font-semibold">Social Page Settings for {metaPageConfig.data?.data?.pageName}</h1>
        <SwitchInput label="All Social Posts Active" register={register} registerName={"isActive"} setValue={handleIsActive} watch={watch} />
      </div>

      <div className="space-y-2">
        <h1 className="font-semibold">Post Type Toggles</h1>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          <SwitchInput label="Evergreens Enabled" register={register} registerName={"socialPageSettings.evergreensEnabled"} setValue={setValue} watch={watch} />
          <SwitchInput label="Infographics Enabled" register={register} registerName={"socialPageSettings.infographicsEnabled"} setValue={setValue} watch={watch} />
          <SwitchInput label="Reviews Enabled" register={register} registerName={"socialPageSettings.reviewsEnabled"} setValue={setValue} watch={watch} />
          <SwitchInput label="News Enabled" register={register} registerName={"socialPageSettings.newsEnabled"} setValue={setValue} watch={watch} />

          <SwitchInput label="Sales Properties Enabled" register={register} registerName={"socialPageSettings.salesPropertiesEnabled"} setValue={setValue} watch={watch} />
          <SwitchInput label="Lettings Properties Enabled" register={register} registerName={"socialPageSettings.lettingsPropertiesEnabled"} setValue={setValue} watch={watch} />
          <SwitchInput label="Sold Properties Enabled" register={register} registerName={"socialPageSettings.soldPropertiesEnabled"} setValue={setValue} watch={watch} />
          <SwitchInput label="Let Properties Enabled" register={register} registerName={"socialPageSettings.letPropertiesEnabled"} setValue={setValue} watch={watch} />

          <SwitchInput label="Reduced Properties Enabled" register={register} registerName={"socialPageSettings.reducedPropertiesEnabled"} setValue={setValue} watch={watch} />
        </div>
      </div>

      <hr />

      <div className="space-y-2">
        <h1 className="font-semibold">Post Limits Per Day</h1>
        <div className="grid grid-cols-2 gap-8">
          <NumberInput label={"Blog Post Limit"} register={register} registerName={"socialPageSettings.blogPostLimit"} />
          <NumberInput label={"Review Limit"} register={register} registerName={"socialPageSettings.reviewLimit"} />
          <NumberInput label={"New Property Limit"} register={register} registerName={"socialPageSettings.newPropertyLimit"} />
          <NumberInput label={"Reduced Property Limit"} register={register} registerName={"socialPageSettings.reducedPropertyLimit"} />
          <NumberInput label={"Sold/Let Property Limit"} register={register} registerName={"socialPageSettings.soldLetPropertyLimit"} />
        </div>
      </div>

      <hr />

      <div className="space-y-2">
        <h1 className="font-semibold">Post Delays (Hours)</h1>
        <div className="grid grid-cols-2 gap-8">
          <NumberInput label={"Blog Post Delay"} register={register} registerName={"socialPageSettings.blogPostDelayHours"} />
          <NumberInput label={"Review Delay"} register={register} registerName={"socialPageSettings.reviewDelayHours"} />
          <NumberInput label={"New Property Delay"} register={register} registerName={"socialPageSettings.newPropertyDelayHours"} />
          <NumberInput label={"Reduced Property Delay"} register={register} registerName={"socialPageSettings.reducedPropertyDelayHours"} />
          <NumberInput label={"Sold/Let Property Delay"} register={register} registerName={"socialPageSettings.soldLetPropertyDelayHours"} />
        </div>
      </div>

      <hr />

      <div className="space-y-2">
        <h1 className="font-semibold">Default Headers and Footers</h1>
        <div className="grid grid-cols-2 gap-8">
          <TextInput label={"Sales Properties Header"} register={register} registerName={"socialPageSettings.salesPropertiesHeader"} richtext rows={4} />
          <TextInput label={"Sales Properties Footer"} register={register} registerName={"socialPageSettings.salesPropertiesFooter"} richtext rows={4} />

          <TextInput label={"Lettings Properties Header"} register={register} registerName={"socialPageSettings.lettingsPropertiesHeader"} richtext rows={4} />
          <TextInput label={"Lettings Properties Footer"} register={register} registerName={"socialPageSettings.lettingsPropertiesFooter"} richtext rows={4} />

          <TextInput label={"Sold Properties Header"} register={register} registerName={"socialPageSettings.soldPropertiesHeader"} richtext rows={4} />
          <TextInput label={"Sold Properties Footer"} register={register} registerName={"socialPageSettings.soldPropertiesFooter"} richtext rows={4} />

          <TextInput label={"Let Properties Header"} register={register} registerName={"socialPageSettings.letPropertiesHeader"} richtext rows={4} />
          <TextInput label={"Let Properties Footer"} register={register} registerName={"socialPageSettings.letPropertiesFooter"} richtext rows={4} />

          <TextInput label={"Reduced Properties Header"} register={register} registerName={"socialPageSettings.reducedPropertiesHeader"} richtext rows={4} />
          <TextInput label={"Reduced Properties Footer"} register={register} registerName={"socialPageSettings.reducedPropertiesFooter"} richtext rows={4} />

          <TextInput label={"Reviews Default Message"} register={register} registerName={"socialPageSettings.reviewsDefaultMessage"} richtext rows={4} />
          <TextInput label={"Infographic Default Message"} register={register} registerName={"socialPageSettings.infographicDefaultMessage"} richtext rows={4} />
        </div>
      </div>

      <div>
        <ButtonInput label={"Save"} isSubmit={true} onClick={() => { }} />
      </div>

    </form>
  )
}